import { FiExternalLink, FiMessageSquare, FiTrendingUp } from 'react-icons/fi'
import { LuBanknote } from 'react-icons/lu'
import type { IconType } from 'react-icons'
import { useAuth } from '../contexts/AuthContext'

// Troca de ambiente (só admin). Os três apps nativos compartilham o login do CRM:
// crm/fin usam o token do CRM direto; a cob troca por sessão nativa dela. O swap
// redireciona pro subdomínio destino levando o token no fragmento `#sso=…` — o app
// destino lê, adota e limpa a URL (fragmento não vai pro servidor/logs).
//
// UI: em vez de um popover flutuante (que cairia atrás do #root, z-index:10000), a
// lista dos outros ambientes sobe de trás do rodapé da sidebar (espaço livre
// abaixo dos itens fixos), como itens ocultos sendo empurrados pra cima.
// Portado do fin (ver vault: conhecimento/sidebar-layout.md).

interface Env { id: 'crm' | 'cob' | 'fin'; label: string; desc: string; url: string; icon: IconType }

const ENVS: Env[] = [
  { id: 'crm', label: 'CRM', desc: 'Central de Relac. e Marketing', url: 'https://crm.twms.com.br', icon: FiMessageSquare },
  { id: 'cob', label: 'ACE', desc: 'Assess. de Cobrança Externa',      url: 'https://cob.twms.com.br', icon: LuBanknote },
  { id: 'fin', label: 'SCF', desc: 'Sist. de Controle Financeiro',     url: 'https://fin.twms.com.br', icon: FiTrendingUp },
]

const CURRENT: Env['id'] = 'crm'

// Em DEV os apps rodam na LAN em portas próprias, fora do Traefik. Sem isto o
// swap do dev jogava o usuário na PRODUÇÃO do outro app — e o token daqui, que
// é do crm-dev, nem seria aceito lá (JWT próprio), então a viagem terminava
// numa tela de login sem explicação.
//
// A detecção é pela PORTA, não pelo host: assim a MESMA imagem serve dev e
// produção (em prod não há porta na URL, nada casa) — a propriedade que o
// ambiente dev inteiro depende pra não virar um build separado.
const DEV_ORIGINS: Partial<Record<Env['id'], string>> = {
  crm: 'http://192.168.44.78:8090',
  fin: 'http://192.168.44.78:8093',
  // cob ainda não tem ambiente dev — ver `envTarget`.
}
const IS_DEV = typeof window !== 'undefined' && ['8090', '8093'].includes(window.location.port)

// Destino do swap. Em dev, só oferece o que TEM par em dev: mandar pra produção
// a partir do dev é o erro que este bloco existe pra impedir.
function envTarget(id: Env['id']): string | null {
  const env = ENVS.find(e => e.id === id)
  if (!env) return null
  if (!IS_DEV) return env.url
  return DEV_ORIGINS[id] ?? null
}

// easeOutExpo: aceleração rápida + desaceleração longa e suave (estilo Apple), sem
// bounce. Curva premium pra revelar/assentar o painel.
const EASE = '[transition-timing-function:cubic-bezier(0.16,1,0.3,1)]'

function goToEnv(id: Env['id']) {
  if (id === CURRENT) return
  const url = envTarget(id)
  if (!url) return // sem par em dev — o item já está desabilitado na lista
  const token = localStorage.getItem('auth-token')
  window.location.href = token ? `${url}/#sso=${encodeURIComponent(token)}` : url
}

// Painel de revelação. NÃO é overlay nem moldura: a lista dos OUTROS ambientes
// (o atual fica de fora — não faz sentido "entrar" onde já se está) sobe de trás
// do rodapé como se empurrasse a sidebar pra cima, revelando itens ocultos. O
// container anima max-height (reserva o espaço) e a lista inteira desliza
// translate-y-full→0 dentro do overflow-hidden — um bloco só, sem cascata.
// Itens no mesmo visual chapado dos itens de navegação.
// O cartão não tem cor nem borda próprias: `.neu-side-pop` pinta o fundo com a
// MESMA cor da sidebar e zera a borda — quem o faz existir é só o box-shadow.
// Como o wrapper recorta na padding-box, a aresta de luz (-2.5px -2.5px 5px)
// precisa de ~7,5px de folga em cima; daí o `my-2` no cartão em vez de `pt-2`
// no wrapper: margem de filho não vaza do BFC e é anulada pelo `max-height:0`,
// enquanto padding (box-sizing:border-box) deixaria 8px de altura ao fechar.
export function EnvSwitchPanel({ open, onClose, collapsed }: { open: boolean; onClose: () => void; collapsed: boolean }) {
  const { user } = useAuth()
  // O fin (SCF) exige a permissão explícita `app_financeiro` — SEM bypass de
  // admin (o backend do fin devolve 403 mesmo pra administradores). Sem ela,
  // nem listamos a entrada, pra não oferecer uma porta que vai bater no 403.
  const hasFin = !!user?.permissions?.toolPermissions?.some(
    t => t.tool_name === 'app_financeiro' && !!t.can_access
  )
  const others = ENVS.filter(e => e.id !== CURRENT && (e.id !== 'fin' || hasFin))
  return (
    <div
      aria-hidden={!open}
      className={[
        'overflow-hidden px-2 transition-[max-height] duration-[550ms]', EASE,
        open ? 'max-h-56' : 'max-h-0 pointer-events-none',
      ].join(' ')}
    >
      <div
        className={[
          'flex flex-col my-2 rounded-xl border border-border bg-secondary/30 neu-side-pop p-1.5 origin-bottom will-change-transform transition-[transform,opacity] duration-[550ms]', EASE,
          open ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-3 scale-[0.97] opacity-0',
        ].join(' ')}
      >
        {!collapsed && (
          <div className="px-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Trocar de ambiente
          </div>
        )}
        {others.map(env => {
          const Icon = env.icon
          // Em dev, ambiente sem par em dev fica VISÍVEL porém desabilitado —
          // esconder faria parecer que o app deixou de existir; mandar pra
          // produção é justamente o que não pode acontecer.
          const alvo = envTarget(env.id)
          const semDev = !alvo
          return (
            <button
              key={env.id}
              disabled={semDev}
              onClick={() => { onClose(); goToEnv(env.id) }}
              title={semDev
                ? `${env.label} não tem ambiente dev — daqui só existiria a produção, e o swap não faz isso`
                : (collapsed ? `${env.label} — ${env.desc}` : `Abrir ${env.label}${IS_DEV ? ' (dev)' : ''}`)}
              className={[
                'w-full flex items-center gap-2.5 rounded-lg border border-border p-2 text-left text-muted-foreground neu-side-pop-item transition-colors',
                semDev ? 'opacity-40 cursor-not-allowed' : 'hover:text-foreground hover:bg-secondary',
                collapsed ? 'justify-center' : '',
              ].join(' ')}
            >
              <span className="flex items-center justify-center w-8 h-8 rounded-lg shrink-0 bg-secondary neu-side-well"><Icon className="w-4 h-4" /></span>
              {!collapsed && (
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="block text-[13px] font-medium text-foreground truncate">{env.label}</span>
                  <span className="block text-[11px] text-muted-foreground truncate">
                    {semDev ? 'sem ambiente dev' : env.desc}
                  </span>
                </span>
              )}
              {!collapsed && !semDev && <FiExternalLink className="w-3 h-3 shrink-0" />}
            </button>
          )
        })}
      </div>
    </div>
  )
}
